import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { finalize, Observable, take, tap } from 'rxjs';

import { CatalogItem } from 'src/app/common/CatalogItem';
import { PersonalizationHome } from 'src/app/common/PersonalizationHome';
import { CallApiService } from 'src/app/services/api-action.service';
import { BookmarkService } from 'src/app/services/bookmark.service';
import { PersonalizationService } from 'src/app/services/personalization.service';
import { AppProfileService } from 'src/app/services/profile.service';

import { CatalogItemCardComponent } from '../catalog-item-card/catalog-item-card.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

/** sessionStorage key of the dismissed opt-in hint — see {@link LandingPageComponent.dismissOptInHint}. */
export const PERSONALIZATION_HINT_DISMISSED_KEY = 'personalizationHintDismissed';

/**
 * #94: the landing page — the personalized entry point above the GetLocal pitch.
 *
 * Three strips, all from ONE call (`GET api/personalization/home`, backend #651) and all
 * rendered through the same `app-catalog-item-card` as the search results:
 *
 * 1. „Zuletzt angesehen" — listings the user opened; recorded by `app-catalog-item-detail`.
 * 2. „Empfehlungen" — derived from the user's own searches, visits and bookmarks. The chain
 *    lives in the backend, not here, or it would exist twice and web and Android would
 *    disagree about the same person. `suggestionsAreFallback` decides the heading: a strip
 *    that is the general catalog entry is never labelled as personalized.
 * 3. „Merkliste" — the newest bookmarks (#652), shown independently of the personalization
 *    switch: showing someone their own saved listings is not profiling (decision 4).
 *
 * <b>Anonymous visitors.</b> The endpoint is `[Authorize]` and this page is public, so
 * without a profile nothing is requested and only the pitch below renders — an anonymous
 * visitor has no history to show anyway.
 *
 * <b>Consent.</b> The page never writes it. While personalization is off the two derived
 * strips are empty resp. the fallback, and the hint under them links to Einstellungen, which
 * is the single place the switch (and its "this deletes your history" warning) lives.
 */
@Component({
  imports: [RouterModule, CatalogItemCardComponent, LoadingSpinnerComponent],
  standalone: true,
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.css'],
})
export class LandingPageComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private profileService = inject(AppProfileService);
  private personalizationService = inject(PersonalizationService);
  private bookmarkService = inject(BookmarkService);
  private apiActionService = inject(CallApiService);

  public home: PersonalizationHome = null;
  public loading = false;
  public bookmarkedIds: ReadonlySet<string> = new Set<string>();

  /** False once the user closes the opt-in hint — see {@link dismissOptInHint}. */
  public optInHintDismissed = false;

  ngOnInit(): void {
    this.optInHintDismissed = this.readHintDismissed();

    this.profileService.currentProfile$.pipe(take(1)).subscribe((profile) => {
      if (!profile?.id) {
        return;
      }

      this.bookmarkService.ensureIdsLoaded();
      this.bookmarkService.bookmarkedIds$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((ids) => (this.bookmarkedIds = ids));

      this.loadHome();
    });
  }

  /** True once a signed-in user's sections have been read — the pitch below renders either way. */
  get hasPersonalization(): boolean {
    return !!this.home;
  }

  get recentlyViewed(): CatalogItem[] {
    return this.home?.recentlyViewed ?? [];
  }

  get suggestions(): CatalogItem[] {
    return this.home?.suggestions ?? [];
  }

  get bookmarks(): CatalogItem[] {
    return this.home?.bookmarks ?? [];
  }

  /**
   * „Beliebt gerade" for the general catalog entry, „Passend zu Ihren Suchen" for a strip
   * actually derived from this user. Never the other way round — an honest label is the
   * whole point of `suggestionsAreFallback`.
   */
  get suggestionsHeading(): string {
    return this.home?.suggestionsAreFallback
      ? $localize`:@@landingPage.suggestions.fallbackHeading:Beliebt gerade`
      : $localize`:@@landingPage.suggestions.heading:Passend zu Ihren Suchen`;
  }

  /**
   * The hint offering the opt-in. Shown while personalization is off and the suggestions are
   * therefore the impersonal fallback — that is exactly when a user would otherwise wonder
   * why nothing here is about them.
   */
  get showOptInHint(): boolean {
    return this.hasPersonalization && !this.home.personalizationEnabled && !this.optInHintDismissed;
  }

  /**
   * Remembered in sessionStorage, not localStorage: this is a UI convenience for the current
   * visit, and the less browser storage this page keeps, the less TTDSG surface it has. A
   * blocked or full storage must not break the page, hence the try/catch on both sides.
   */
  public dismissOptInHint(): void {
    this.optInHintDismissed = true;
    try {
      sessionStorage.setItem(PERSONALIZATION_HINT_DISMISSED_KEY, '1');
    } catch {
      // Private mode / storage disabled — the hint simply comes back next navigation.
    }
  }

  /**
   * The card's `[asyncClick]` factory, shared by all three strips. Un-bookmarking takes the
   * tile off the Merkliste strip wherever the click came from, so the strips cannot end up
   * disagreeing about the same listing; the other two strips keep their tile and only flip
   * the icon, exactly as the search results do.
   */
  public bookmarkAction(catalogSkuId: string): () => Observable<unknown> {
    return () =>
      this.apiActionService.callWithToasts$(
        this.bookmarkService.toggle(catalogSkuId).pipe(
          tap(() => {
            if (!this.bookmarkService.isBookmarked(catalogSkuId)) {
              this.removeFromBookmarkStrip(catalogSkuId);
            }
          }),
        ),
        null,
        $localize`:@@catalog.bookmark.error:Die Merkliste konnte nicht geändert werden.`,
      );
  }

  /**
   * Deliberately silent on failure: the personalized strips are an addition to the landing
   * page, never a precondition for it. A failing read leaves `home` null and the page shows
   * the pitch alone rather than an error where the user expected their own listings.
   */
  private loadHome(): void {
    this.loading = true;
    this.personalizationService
      .getHome()
      .pipe(
        take(1),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (home) => (this.home = home),
        error: () => (this.home = null),
      });
  }

  private removeFromBookmarkStrip(catalogSkuId: string): void {
    if (this.home) {
      this.home = {
        ...this.home,
        bookmarks: this.home.bookmarks.filter((i) => i.catalogSku?.skuId !== catalogSkuId),
      };
    }
  }

  private readHintDismissed(): boolean {
    try {
      return sessionStorage.getItem(PERSONALIZATION_HINT_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  }
}
